import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { LegalBody, LegalSection } from "@/components/LegalPage";
import { SITE } from "@/lib/site";

export default function TermsOfUse() {
  return (
    <>
      <Seo
        title="Terms of Use | AutoNeova"
        description="Terms governing use of the AutoNeova corporate website, including informational purpose, third-party links, Amazon transactions, and limitation of liability."
        path="/terms-of-use"
      />
      <PageHeader
        testId="terms-page-header"
        eyebrow="Legal"
        title="Terms of Use"
        intro={`Effective Date: ${SITE.effectiveDate}`}
      />
      <LegalBody testId="terms-page-content">
        <p className="text-base leading-relaxed text-[#263238]">
          These Terms of Use apply to your use of this website. By using this website, you agree to
          these terms.
        </p>

        <LegalSection heading="1. Informational Purpose">
          <p>
            This website is provided for general information about AutoNeova and its product
            categories. It does not process orders, payments, or returns, and it does not provide
            technical, mechanical, or professional advice.
          </p>
        </LegalSection>

        <LegalSection heading="2. Accuracy and Availability of Information">
          <p>
            We aim to keep the information on this website accurate and current. Product categories,
            descriptions, and other content may change without notice, and the website may be
            unavailable from time to time for maintenance or technical reasons.
          </p>
        </LegalSection>

        <LegalSection heading="3. Third-Party Links">
          <p>
            This website contains links to third-party websites, including Amazon. Those websites are
            operated independently of AutoNeova, and AutoNeova is not responsible for their content,
            policies, or practices.
          </p>
        </LegalSection>

        <LegalSection heading="4. Amazon Transactions">
          <p>
            Purchases made through Amazon are governed by the relevant Amazon listing, seller terms,
            and Amazon policies. Pricing, availability, compatibility information, delivery terms,
            returns, and customer support for an order are handled through Amazon.
          </p>
          <p>
            AutoNeova sells as an independent third-party seller. Nothing on this website should be
            read as a claim of affiliation with, sponsorship by, or endorsement from Amazon or any
            vehicle manufacturer.
          </p>
        </LegalSection>

        <LegalSection heading="5. Intellectual Property">
          <p>
            The AutoNeova name and the content of this website are the property of AutoNeova, except
            where otherwise noted. Third-party names and marks referenced on this website remain the
            property of their respective owners and are used only for identification purposes.
          </p>
        </LegalSection>

        <LegalSection heading="6. Limitation of Liability">
          <p>
            This website is provided on an "as is" and "as available" basis. To the extent permitted
            by applicable law, AutoNeova is not liable for indirect or incidental losses arising from
            use of, or inability to use, this website.
          </p>
        </LegalSection>

        <LegalSection heading="7. Changes to the Website and These Terms">
          <p>
            AutoNeova may update this website and these Terms of Use. The current version will always
            be posted on this page.
          </p>
        </LegalSection>

        <LegalSection heading="8. Contact Information">
          <p>
            Questions about these Terms of Use may be sent to{" "}
            <a
              href={`mailto:${SITE.email}`}
              data-testid="terms-email-link"
              className="font-semibold text-[#1F6FEB] underline decoration-[#1F6FEB]/30 underline-offset-4 hover:decoration-[#1F6FEB]"
            >
              {SITE.email}
            </a>
            .
          </p>
          <div
            data-testid="terms-company-info"
            className="rounded-lg border border-[#0B1F33]/10 bg-[#F4F6F8] p-6 text-sm leading-relaxed text-[#263238]"
          >
            <p className="font-semibold text-[#0B1F33]">{SITE.legalName}</p>
            <p>Brand: {SITE.brand}</p>
            <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-[#263238]/60">
              Registered Business Address
            </p>
            <address className="not-italic">
              {SITE.address.line1}
              <br />
              {`${SITE.address.city}, ${SITE.address.state} ${SITE.address.postalCode}`}
              <br />
              {SITE.address.country}
            </address>
            <p className="mt-2">
              Email:{" "}
              <a
                href={`mailto:${SITE.email}`}
                className="font-medium text-[#1F6FEB] underline decoration-[#1F6FEB]/30 underline-offset-4 hover:decoration-[#1F6FEB]"
              >
                {SITE.email}
              </a>
            </p>
            <p>Website: {SITE.website}</p>
            <p>
              Amazon sales page:{" "}
              <a
                href={SITE.amazonStore}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="terms-amazon-link"
                className="break-all font-medium text-[#1F6FEB] underline decoration-[#1F6FEB]/30 underline-offset-4 hover:decoration-[#1F6FEB]"
              >
                {SITE.amazonStore}
              </a>
            </p>
          </div>
        </LegalSection>
      </LegalBody>
    </>
  );
}
